import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import connect from 'helpers/apiConnect';
import { loadCountries } from 'redux/reducers/countries';

import getChartPositionsUrl from 'url-constructors/getChartPositionsUrl';
import getChartsKPIURL from 'url-constructors/getChartsKPIURL';

import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import useMedia from 'hooks/useMedia';
import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';

import OverviewContainer from 'components/Overview/Container/Container';
import useFilteredCharts from 'components/data-containers/PositionsTable/shared/hooks/useFilteredCharts';
import useChartPositions from 'components/data-containers/PositionsTable/shared/hooks/useChartPositions';
import OfficialChartsTable from './OfficialChartsTable';
import { PromiseState } from 'react-refetch';
import { EntityTypes, CompareModes } from 'context/entityContext';
import { RootState } from 'redux/rootReducer';
import { ChartTypes, ProductTypes } from 'types/shared';
import { ITopicFilterState } from 'components/TopicFilters/shared';
import { ITerritoryDataItem } from './../types';

interface IKPIUrlArgs {
  entityIds: string[];
  entityType: EntityTypes;
  countryIds?: string | string[];
  chartType: ChartTypes;
}

interface IOuterProps {
  entityType: EntityTypes;
  productType: ProductTypes;
  filters: ITopicFilterState;
  entityIds: string[];
  compareMode: CompareModes;
  countryIds: string[];
}

function dataSources(props: any): any {
  const { entityIds, entityType, compareMode, countryIds, filters } = props;

  const getKpiUrlArgs = (chartType: ChartTypes): IKPIUrlArgs => {
    return {
      entityIds,
      entityType,
      countryIds: compareMode === 'country' ? countryIds : filters.country,
      chartType: chartType,
    };
  };

  const officialWeeklyUrl = getChartPositionsUrl({
    ...props,
    chartFrequency: 'weekly',
    chartType: 'official',
  });
  const airplayDailyUrl = getChartPositionsUrl({
    ...props,
    chartFrequency: 'daily',
    chartType: 'airplay',
  });

  return {
    officialWeeklyChartPositions: officialWeeklyUrl,
    airplayDailyChartPositions: airplayDailyUrl,
    officialKpiFetch: getChartsKPIURL(getKpiUrlArgs('official')),
    airplayKpiFetch: getChartsKPIURL(getKpiUrlArgs('airplay')),
  };
}

interface IEntity {
  id: string;
  type: EntityTypes;
}

interface IChartPositions {
  entityData: {
    charts: {
      chartDisplayName: string;
      chartId: number;
      chartName: string;
      chartType: string;
      customerId: number;
      customerName: string;
      productDisplayName: string;
      productId: string;
      productType: ProductTypes;
      territoryData: ITerritoryDataItem[];
      totalChartStreams: number;
      totalNumberOfCountries: number;
    }[];
    entity: IEntity;
    totalChartsCount: number;
  }[];
}

interface IKpiFetchData {
  entity: IEntity;
  data: {
    globalChartCount: number | null;
    globalStreamCount: number | null;
    localChartCount: number | null;
    localStreamCount: number | null;
    numberOfChartingAlbums: number | null;
    numberOfChartingTracks: number | null;
    totalChartCount: number | null;
    totalChartStreams: number | null;
  }[];
}

interface IOfficialChartPerformanceProps extends IOuterProps {
  officialWeeklyChartPositions: PromiseState<IChartPositions[]>;
  airplayDailyChartPositions: PromiseState<IChartPositions[]>;
  officialKpiFetch: PromiseState<IKpiFetchData[]>;
  airplayKpiFetch: PromiseState<IKpiFetchData[]>;
}

/**
 * Renders the top 5 official charts position details and the total count of official positions
 * Renders a `No data available` placeholder in the absence of data.
 */
const OfficialChartPerformance: React.FC<IOfficialChartPerformanceProps> = (
  props
) => {
  const {
    officialWeeklyChartPositions,
    airplayDailyChartPositions,
    officialKpiFetch,
    airplayKpiFetch,
    entityType,
    productType,
    filters,
  } = props;

  const [, isPending] = useCombinedRefetchState([
    officialWeeklyChartPositions,
    airplayDailyChartPositions,
    officialKpiFetch,
    airplayKpiFetch,
  ]);

  const isLargeScreen = useMedia('(min-width: 60em)');
  const showProductColumn = entityType === 'artist' || entityType === 'project';

  // Load the country metadata on mount.
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadCountries());
  }, [dispatch]);

  const countries = useSelector((state: RootState) => state.countries.data);

  const charts = useChartPositions(
    airplayDailyChartPositions,
    officialWeeklyChartPositions,
    countries,
    'currentPosition'
  );

  const filteredCharts = useFilteredCharts(charts, filters);

  const renderedRows = filteredCharts?.slice(0, 5);

  return (
    <OverviewContainer
      filtersToShow={['country']}
      title="Official Charts"
      linkTo={useUrlForTopic('charts/official')}
    >
      <OfficialChartsTable
        loading={isPending}
        charts={renderedRows}
        isSmallScreen={!isLargeScreen}
        productType={productType}
        showProductColumn={showProductColumn}
      />
    </OverviewContainer>
  );
};

export default connect<IOfficialChartPerformanceProps>(dataSources)(
  OfficialChartPerformance
) as any;
